import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AngolaPaymentSettings } from "@/components/AngolaPaymentSettings";
import { StripeSettings } from "@/components/StripeSettings";
import { BankSettings } from "@/components/BankSettings";
import { TransferReviews } from "@/components/TransferReviews";
import { CourierDelivery } from "@/components/CourierDelivery";
import { toast } from "sonner";
import { Plus, Edit, Trash2, TrendingUp, Package, Users, ShoppingBag, DollarSign, Megaphone, Settings as SettingsIcon } from "lucide-react";

const emptyProduct = { name: "", slug: "", description: "", category_slug: "smartphones", price_eur: 0, sale_price_eur: null, image: "", stock: 0, rating: 4.5, reviews_count: 0, colors: [], sizes: [], featured: false, active: true, weight_g: 500, gallery: [] };
const emptyAd = { title: "", subtitle: "", image: "", button_text: "Ver", button_link: "/produtos", placement: "home_top", active: true, countries: [] };
const emptyPromo = { code: "", kind: "percent", value: 10, min_subtotal: 0, max_uses: 0, active: true, countries: [], categories: [] };

export default function Admin() {
  const nav = useNavigate();
  const { user, authLoaded } = useApp();
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [ads, setAds] = useState([]);
  const [promos, setPromos] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    if (!authLoaded) return;
    if (!user || user.role !== "admin") { nav("/login"); return; }
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoaded, nav]);

  const reload = async () => {
    const [s, p, c, o, a, pr, st] = await Promise.all([
      api.get("/admin/stats"), api.get("/products", { params: { limit: 200 } }),
      api.get("/categories"), api.get("/orders"),
      api.get("/ads/admin"), api.get("/promos"), api.get("/settings"),
    ]);
    setStats(s.data); setProducts(p.data); setCategories(c.data);
    setOrders(o.data); setAds(a.data); setPromos(pr.data); setSettings(st.data);
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div className="mx-auto max-w-7xl px-4 py-10">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-primary mb-1">Painel Admin</div>
        <h1 className="text-4xl font-display font-bold">Gestão VOLTIX</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="stats" data-testid="tab-stats">Estatísticas</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">Produtos</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Encomendas</TabsTrigger>
          <TabsTrigger value="ads" data-testid="tab-ads">Anúncios</TabsTrigger>
          <TabsTrigger value="promos" data-testid="tab-promos">Códigos</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings"><SettingsIcon className="w-3.5 h-3.5 mr-1" />Definições</TabsTrigger>
        </TabsList>

        <TabsContent value="stats">
          {stats && (
            <>
              <div className="grid md:grid-cols-4 gap-4 mb-8">
                <StatCard icon={DollarSign} label="Receita" value={`€${stats.revenue.toFixed(2)}`} />
                <StatCard icon={ShoppingBag} label="Encomendas" value={stats.total_orders} />
                <StatCard icon={Users} label="Clientes" value={stats.customers} />
                <StatCard icon={Package} label="Produtos" value={stats.products} />
              </div>
              <div className="rounded-2xl border border-border p-6 bg-card">
                <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary" /> Produtos mais vendidos</h3>
                {stats.top_products.length === 0 ? <p className="text-sm text-muted-foreground">Sem vendas ainda.</p> :
                  <ul className="space-y-2">{stats.top_products.map((t, i) => <li key={i} className="flex justify-between text-sm"><span>{t.name}</span><span className="font-mono">{t.sold}</span></li>)}</ul>}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="products">
          <ProductManager products={products} categories={categories} onReload={reload} />
        </TabsContent>

        <TabsContent value="orders">
          <CourierDelivery orders={orders} onReload={reload} />
          <TransferReviews orders={orders} onReload={reload} />
          <div className="rounded-2xl border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary"><tr><th className="text-left p-3">Número</th><th className="text-left p-3">Validação da entrega</th><th className="text-left p-3">Cliente</th><th className="text-left p-3">Total</th><th className="text-left p-3">Estado</th><th className="text-left p-3">Pagamento</th><th className="p-3">Data</th></tr></thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-t border-border" data-testid={`admin-order-${o.number}`}>
                    <td className="p-3 font-mono text-xs">{o.number}</td>
                    <td className="p-3 text-xs text-muted-foreground">{o.delivery?.status === "completed" ? "Concluída" : o.delivery?.status === "verified" ? "Validada" : "Por validar"}</td>
                    <td className="p-3">{o.email}</td>
                    <td className="p-3">{o.pricing.total.toFixed(2)} {o.currency}</td>
                    <td className="p-3">
                      <Select value={o.status} disabled={["delivered", "cancelled", "refunded"].includes(o.status)} onValueChange={async (v) => { try { await api.put(`/orders/${o.number}/status`, { status: v }); toast.success("Estado atualizado"); reload(); } catch (e) { toast.error(e.response?.data?.detail || "Erro ao atualizar"); } }}>
                        <SelectTrigger className="h-8 w-44" data-testid={`order-status-${o.number}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries({ pending: "Aguarda 1.ª parcela", paid_partial: "1.ª parcela paga", processing: "Em preparação", shipped: "Expedida", delivery_pending: "Estafeta presente", paid: "Paga", delivered: "Entregue", cancelled: "Cancelada", refunded: "Reembolsada" }).map(([s, label]) => <SelectItem key={s} value={s} disabled={!["processing", "shipped", "cancelled", "refunded"].includes(s)} data-testid={`status-option-${o.number}-${s}`}>{label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs ${o.payment_status==="paid" ? "bg-green-500/15 text-green-600" : "bg-yellow-500/15 text-yellow-700"}`}>{o.payment_status}</span></td>
                    <td className="p-3 text-center text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString("pt-PT")}</td>
                  </tr>
                ))}
                {orders.length === 0 && <tr><td colSpan="7" className="text-center p-8 text-muted-foreground">Sem encomendas.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="ads"><AdManager ads={ads} onReload={reload} /></TabsContent>
        <TabsContent value="promos"><PromoManager promos={promos} onReload={reload} categories={categories} /></TabsContent>
        <TabsContent value="settings"><AngolaPaymentSettings /><StripeSettings /><BankSettings settings={settings} onReload={reload} /><SettingsManager settings={settings} onReload={reload} /></TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-border p-5 bg-card">
      <Icon className="w-5 h-5 text-primary mb-2" />
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-2xl font-display font-bold mt-1">{value}</div>
    </div>
  );
}

function ProductManager({ products, categories, onReload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyProduct);
  const isEdit = !!form.id;

  const save = async () => {
    try {
      const { id, ...body } = form;
      body.price_eur = Number(body.price_eur);
      body.sale_price_eur = body.sale_price_eur ? Number(body.sale_price_eur) : null;
      body.stock = Number(body.stock);
      body.weight_g = Number(body.weight_g) || 500;
      body.rating = Number(body.rating) || 4.5;
      body.reviews_count = Number(body.reviews_count) || 0;
      body.colors = typeof body.colors === "string" ? body.colors.split(",").map(s=>s.trim()).filter(Boolean) : (body.colors || []);
      body.sizes = typeof body.sizes === "string" ? body.sizes.split(",").map(s=>s.trim()).filter(Boolean) : (body.sizes || []);
      body.gallery = body.gallery || [];
      if (isEdit) await api.put(`/products/${id}`, body); else await api.post("/products", body);
      toast.success("Produto guardado");
      setOpen(false); setForm(emptyProduct); onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const remove = async (id) => { if (!window.confirm("Eliminar?")) return; await api.delete(`/products/${id}`); toast.success("Eliminado"); onReload(); };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={() => setForm(emptyProduct)} className="rounded-full btn-primary" data-testid="new-product-btn"><Plus className="w-4 h-4 mr-1" /> Novo produto</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{isEdit ? "Editar" : "Novo"} produto</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Nome</Label><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value,slug:e.target.value.toLowerCase().replace(/[^a-z0-9]+/g,"-")})} /></div>
              <div><Label>Slug</Label><Input value={form.slug} onChange={(e)=>setForm({...form,slug:e.target.value})} /></div>
              <div><Label>Categoria</Label><Select value={form.category_slug} onValueChange={(v)=>setForm({...form,category_slug:v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{categories.map(c=><SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Preço (EUR)</Label><Input type="number" step="0.01" value={form.price_eur} onChange={(e)=>setForm({...form,price_eur:e.target.value})} /></div>
              <div><Label>Preço promo (EUR)</Label><Input type="number" step="0.01" value={form.sale_price_eur || ""} onChange={(e)=>setForm({...form,sale_price_eur:e.target.value || null})} /></div>
              <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={(e)=>setForm({...form,stock:e.target.value})} /></div>
              <div><Label>Peso (g)</Label><Input type="number" value={form.weight_g} onChange={(e)=>setForm({...form,weight_g:e.target.value})} /></div>
              <div className="col-span-2"><Label>Imagem (URL)</Label><Input value={form.image} onChange={(e)=>setForm({...form,image:e.target.value})} /></div>
              <div><Label>Cores (vírgula)</Label><Input value={Array.isArray(form.colors) ? form.colors.join(", ") : form.colors} onChange={(e)=>setForm({...form,colors:e.target.value})} /></div>
              <div><Label>Tamanhos (vírgula)</Label><Input value={Array.isArray(form.sizes) ? form.sizes.join(", ") : form.sizes} onChange={(e)=>setForm({...form,sizes:e.target.value})} /></div>
              <div className="col-span-2"><Label>Descrição</Label><Textarea rows={3} value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} /></div>
              <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e)=>setForm({...form,featured:e.target.checked})} /> Destaque</label>
            </div>
            <Button onClick={save} className="w-full rounded-full btn-primary mt-2" data-testid="save-product-btn">Guardar</Button>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {products.map((p) => (
          <div key={p.id} className="rounded-2xl border border-border p-4 bg-card flex gap-3">
            <img src={p.image} alt={p.name} className="w-20 h-20 rounded-xl object-cover" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{p.name}</div>
              <div className="text-xs text-muted-foreground">{p.category_slug} • €{(p.sale_price_eur ?? p.price_eur).toFixed(2)} • stock {p.stock}</div>
              <div className="mt-2 flex gap-2">
                <Button variant="outline" size="sm" onClick={()=>{setForm({...p, colors:(p.colors||[]).join(", "), sizes:(p.sizes||[]).join(", ")}); setOpen(true);}} data-testid={`edit-product-${p.slug}`}><Edit className="w-3.5 h-3.5" /></Button>
                <Button variant="outline" size="sm" onClick={()=>remove(p.id)}><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdManager({ ads, onReload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyAd);
  const save = async () => {
    try {
      const { id, views, clicks, created_at, ...body } = form;
      if (id) await api.put(`/ads/${id}`, body); else await api.post("/ads", body);
      toast.success("Anúncio guardado"); setOpen(false); setForm(emptyAd); onReload();
    } catch { toast.error("Erro"); }
  };
  const remove = async (id) => { if (!window.confirm("Eliminar?")) return; await api.delete(`/ads/${id}`); onReload(); };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={()=>setForm(emptyAd)} className="rounded-full btn-primary" data-testid="new-ad-btn"><Megaphone className="w-4 h-4 mr-1" /> Novo anúncio</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{form.id ? "Editar" : "Novo"} anúncio</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Título</Label><Input value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} /></div>
              <div><Label>Subtítulo</Label><Input value={form.subtitle} onChange={(e)=>setForm({...form,subtitle:e.target.value})} /></div>
              <div><Label>Imagem URL</Label><Input value={form.image} onChange={(e)=>setForm({...form,image:e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Texto botão</Label><Input value={form.button_text} onChange={(e)=>setForm({...form,button_text:e.target.value})} /></div>
                <div><Label>Link</Label><Input value={form.button_link} onChange={(e)=>setForm({...form,button_link:e.target.value})} /></div>
              </div>
              <div><Label>Placement</Label>
                <Select value={form.placement} onValueChange={(v)=>setForm({...form,placement:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="home_top">Topo</SelectItem><SelectItem value="home_middle">Meio</SelectItem><SelectItem value="top_bar">Barra superior</SelectItem></SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e)=>setForm({...form,active:e.target.checked})} /> Ativo</label>
              <Button onClick={save} className="w-full rounded-full btn-primary">Guardar</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-3">
        {ads.map((a) => (
          <div key={a.id} className="rounded-2xl border border-border p-4 bg-card flex items-center gap-3">
            {a.image && <img src={a.image} alt="" className="w-16 h-16 rounded object-cover" />}
            <div className="flex-1"><div className="font-semibold">{a.title}</div><div className="text-xs text-muted-foreground">{a.placement} • {a.active ? "ativo" : "pausado"} • {a.views} views / {a.clicks} cliques</div></div>
            <Button variant="outline" size="sm" onClick={()=>{setForm(a); setOpen(true);}}><Edit className="w-3.5 h-3.5" /></Button>
            <Button variant="outline" size="sm" onClick={()=>remove(a.id)}><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function PromoManager({ promos, onReload, categories }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyPromo);
  const save = async () => {
    try {
      const { id, used, created_at, ...body } = form;
      body.value = Number(body.value); body.min_subtotal = Number(body.min_subtotal); body.max_uses = Number(body.max_uses);
      body.categories = body.categories || [];
      if (id) await api.put(`/promos/${id}`, body); else await api.post("/promos", body);
      toast.success("Código guardado"); setOpen(false); setForm(emptyPromo); onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const remove = async (id) => { if (!window.confirm("Eliminar?")) return; await api.delete(`/promos/${id}`); onReload(); };
  const toggleCat = (slug) => {
    setForm((f) => {
      const cats = f.categories || [];
      return { ...f, categories: cats.includes(slug) ? cats.filter((c) => c !== slug) : [...cats, slug] };
    });
  };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={()=>setForm(emptyPromo)} className="rounded-full btn-primary" data-testid="new-promo-btn"><Plus className="w-4 h-4 mr-1" /> Novo código</Button></DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{form.id ? "Editar" : "Novo"} código</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Código</Label><Input value={form.code} onChange={(e)=>setForm({...form,code:e.target.value.toUpperCase()})} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Tipo</Label><Select value={form.kind} onValueChange={(v)=>setForm({...form,kind:v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="percent">Percentagem</SelectItem><SelectItem value="fixed">Valor fixo</SelectItem></SelectContent></Select></div>
                <div><Label>Valor</Label><Input type="number" value={form.value} onChange={(e)=>setForm({...form,value:e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Subtotal mínimo (EUR)</Label><Input type="number" value={form.min_subtotal} onChange={(e)=>setForm({...form,min_subtotal:e.target.value})} /></div>
                <div><Label>Máx utilizações (0=∞)</Label><Input type="number" value={form.max_uses} onChange={(e)=>setForm({...form,max_uses:e.target.value})} /></div>
              </div>
              <div>
                <Label>Aplica-se a categorias (vazio = todas)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(categories || []).map((c) => {
                    const on = (form.categories || []).includes(c.slug);
                    return (
                      <button key={c.slug} type="button" onClick={() => toggleCat(c.slug)}
                        data-testid={`promo-cat-${c.slug}`}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
                        {c.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e)=>setForm({...form,active:e.target.checked})} /> Ativo</label>
              <Button onClick={save} className="w-full rounded-full btn-primary" data-testid="save-promo-btn">Guardar</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-2">
        {promos.map((p) => (
          <div key={p.id} className="rounded-2xl border border-border p-4 bg-card flex items-center gap-3" data-testid={`promo-row-${p.code}`}>
            <div className="flex-1">
              <span className="font-mono font-bold text-primary">{p.code}</span>
              <span className="text-xs text-muted-foreground ml-2">{p.kind === "percent" ? `${p.value}%` : `€${p.value}`} • usado {p.used}{p.max_uses ? `/${p.max_uses}` : ""}</span>
              {p.categories?.length > 0 && (
                <div className="text-[10px] text-muted-foreground mt-1">Categorias: {p.categories.join(", ")}</div>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={()=>{setForm({ ...p, categories: p.categories || [] }); setOpen(true);}}><Edit className="w-3.5 h-3.5" /></Button>
            <Button variant="outline" size="sm" onClick={()=>remove(p.id)}><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsManager({ settings, onReload }) {
  const [form, setForm] = React.useState(null);
  React.useEffect(() => { setForm(settings ? JSON.parse(JSON.stringify(settings)) : null); }, [settings]);

  if (!form) return <div className="p-8 text-muted-foreground">A carregar...</div>;

  const save = async () => {
    try {
      const { id, ...body } = form;
      await api.put("/settings", body);
      toast.success("Definições atualizadas");
      onReload();
    } catch { toast.error("Erro"); }
  };

  const setCurrency = (code, field, value) => {
    setForm({ ...form, currencies: { ...form.currencies, [code]: { ...form.currencies[code], [field]: value } } });
  };
  const setShipping = (idx, field, value) => {
    const s = [...form.shipping_rules]; s[idx] = { ...s[idx], [field]: value };
    setForm({ ...form, shipping_rules: s });
  };
  const setTax = (idx, value) => {
    const s = [...form.tax_rules]; s[idx] = { ...s[idx], rate: parseFloat(value) || 0 };
    setForm({ ...form, tax_rules: s });
  };
  const setBracket = (idx, field, value) => {
    const b = [...(form.weight_brackets || [])];
    b[idx] = { ...b[idx], [field]: parseFloat(value) || 0 };
    setForm({ ...form, weight_brackets: b });
  };

  return (
    <div className="space-y-8" data-testid="settings-panel">
      <div className="rounded-2xl border border-border p-6 bg-card">
        <h3 className="font-display font-semibold text-lg mb-4 flex items-center gap-2"><SettingsIcon className="w-4 h-4 text-primary" /> Moedas &amp; taxas</h3>
        <div className="grid gap-4">
          {Object.entries(form.currencies).map(([code, c]) => (
            <div key={code} className="grid grid-cols-4 gap-3 items-center" data-testid={`currency-row-${code}`}>
              <div className="font-mono font-bold">{code}</div>
              <div>
                <Label className="text-xs">Símbolo</Label>
                <Input value={c.symbol} onChange={(e) => setCurrency(code, "symbol", e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">1 EUR = ? {code}</Label>
                <Input type="number" step="0.0001" value={c.rate} onChange={(e) => setCurrency(code, "rate", parseFloat(e.target.value) || 1)} data-testid={`rate-${code}`} />
              </div>
              <div>
                <Label className="text-xs">Nome</Label>
                <Input value={c.name} onChange={(e) => setCurrency(code, "name", e.target.value)} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-border p-6 bg-card">
        <h3 className="font-display font-semibold text-lg mb-4">Envio por país (EUR)</h3>
        <div className="grid gap-4">
          {form.shipping_rules.map((r, i) => (
            <div key={r.country} className="grid grid-cols-4 gap-3 items-center" data-testid={`shipping-row-${r.country}`}>
              <div className="font-mono font-bold">{r.country}</div>
              <div><Label className="text-xs">Standard €</Label><Input type="number" step="0.01" value={r.standard} onChange={(e) => setShipping(i, "standard", parseFloat(e.target.value) || 0)} data-testid={`shipping-standard-${r.country}`} /></div>
              <div className="col-span-2"><Label className="text-xs">Dias</Label><Input value={r.days} onChange={(e) => setShipping(i, "days", e.target.value)} data-testid={`shipping-days-${r.country}`} /></div>
            </div>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Portes grátis a partir de (EUR)</Label>
            <Input type="number" step="0.01" value={form.free_shipping_threshold_eur} onChange={(e) => setForm({ ...form, free_shipping_threshold_eur: parseFloat(e.target.value) || 0 })} data-testid="free-shipping-threshold" />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border p-6 bg-card">
        <h3 className="font-display font-semibold text-lg mb-4">Escalões por peso (adicional em EUR)</h3>
        <p className="text-xs text-muted-foreground mb-4">Somado ao envio base. Cada linha aplica-se até ao peso indicado.</p>
        <div className="grid gap-3">
          <div className="grid grid-cols-4 gap-2 text-xs text-muted-foreground">
            <div>Até (g)</div><div>PT Standard</div><div>AO Standard</div><div></div>
          </div>
          {(form.weight_brackets || []).map((b, i) => (
            <div key={i} className="grid grid-cols-4 gap-2 items-center" data-testid={`bracket-row-${i}`}>
              <Input type="number" value={b.max_weight_g} onChange={(e) => setBracket(i, "max_weight_g", e.target.value)} data-testid={`bracket-max-${i}`} />
              <Input type="number" step="0.01" value={b.PT_standard} onChange={(e) => setBracket(i, "PT_standard", e.target.value)} data-testid={`bracket-pt-std-${i}`} />
              <Input type="number" step="0.01" value={b.AO_standard} onChange={(e) => setBracket(i, "AO_standard", e.target.value)} data-testid={`bracket-ao-std-${i}`} />
              <Button variant="outline" size="sm" onClick={() => {
                const nb = (form.weight_brackets || []).filter((_, x) => x !== i);
                setForm({ ...form, weight_brackets: nb });
              }}><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" className="mt-3 rounded-full" onClick={() => setForm({ ...form, weight_brackets: [...(form.weight_brackets || []), { max_weight_g: 10000, PT_standard: 0, AO_standard: 0 }] })} data-testid="add-bracket-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar escalão
        </Button>
      </div>

      <div className="rounded-2xl border border-border p-6 bg-card">
        <h3 className="font-display font-semibold text-lg mb-4">IVA por país</h3>
        <div className="grid gap-3">
          {form.tax_rules.map((r, i) => (
            <div key={r.country} className="grid grid-cols-3 gap-3 items-center" data-testid={`tax-row-${r.country}`}>
              <div className="font-mono font-bold">{r.country}</div>
              <div><Label className="text-xs">Taxa (0-1, ex: 0.23 = 23%)</Label><Input type="number" step="0.01" value={r.rate} onChange={(e) => setTax(i, e.target.value)} /></div>
              <div className="text-sm text-muted-foreground">= {(r.rate * 100).toFixed(1)}%</div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-border p-6 bg-card">
        <h3 className="font-display font-semibold text-lg mb-4">Contacto</h3>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Telefone</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></div>
          <div><Label className="text-xs">Email</Label><Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
          <div className="col-span-2"><Label className="text-xs">Horário</Label><Input value={form.business_hours} onChange={(e) => setForm({ ...form, business_hours: e.target.value })} /></div>
        </div>
      </div>

      <Button size="lg" className="rounded-full btn-primary" onClick={save} data-testid="save-settings-btn">
        Guardar definições
      </Button>
    </div>
  );
}

